// Ventas netas acumuladas del día (día UTC, como el CRM), hora a hora: hoy (línea gruesa, hasta ahora),
// ayer y la semana pasada (discontinua). Las horas se rotulan en UTC. SVG propio: tres
// líneas no justifican una librería de gráficas. Se dibuja al tamaño real de su hueco (medido), así
// ocupa toda su caja sin estirar letras.
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { delta, dineroExacto, escalaRedonda, horasEje, horaUtc, miles } from './formato';
import { Etiqueta, FOCO, hastaHora, usePuntero, type FilaEtiqueta } from './Puntero';
import type { DiaSala } from './tipos';
import { useTamano } from './useTamano';

const ANCHO = 960;
const ALTO = 260;
const ALTO_MIN = 140;
const IZQ = 56;
const ABAJO = 26;
const ARRIBA = 10;
const DER = 20;

export function GraficoAcumulado({
  acumulado,
  horaHoy,
  referencia,
  etiqueta,
  formatoEje = (v: number) => `${miles(v)} k$`,
  formato = dineroExacto,
  prevision,
}: {
  acumulado: Record<DiaSala, number[]>;
  /** Hora UTC decimal en la que acaba la línea de hoy. */
  horaHoy: number | null;
  /** Instante de la foto: fija qué día UTC se rotula. */
  referencia: Date;
  etiqueta: string;
  /** Rótulo del eje (por defecto, miles de dólares). */
  formatoEje?: (v: number) => string;
  /** Cifra exacta al pasar el ratón (por defecto, dólares). */
  formato?: (v: number) => string;
  /** 22-10 (revisión de la sala): cómo acabará hoy a este ritmo, dibujado de «ahora» a medianoche con su horquilla. */
  prevision?: { total: number; bajo: number; alto: number } | null;
}) {
  const [ref, tam] = useTamano<HTMLDivElement>(ALTO_MIN);
  const ancho = tam?.ancho ?? ANCHO;
  const alto = tam?.alto ?? ALTO;
  const valores = [...(['hoy', 'ayer', 'sem'] as const).flatMap((k) => acumulado[k]), ...(prevision ? [prevision.alto] : [])];
  const { tope: max, marcas } = escalaRedonda(Math.max(0, ...valores) || 1000, 4);
  const w = ancho - IZQ - DER;
  const h = alto - ABAJO - ARRIBA;
  const x = (hora: number) => IZQ + (w * hora) / 24;
  const y = (v: number) => ARRIBA + h * (1 - v / max);
  const ultimoHoy = acumulado.hoy.length - 1;
  // El último punto de hoy va en la hora exacta de ahora, no al final de su hora.
  const horaDe = (k: DiaSala, i: number) => (k === 'hoy' && i === ultimoHoy && horaHoy !== null ? Math.min(i, horaHoy) : i);
  const linea = (k: DiaSala) => acumulado[k].map((v, i) => `${x(horaDe(k, i)).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const valorHoy = acumulado.hoy[ultimoHoy];
  const horas = Math.max(acumulado.hoy.length, acumulado.ayer.length, acumulado.sem.length) - 1;
  const p = usePuntero({ n: horas + 1, ancho, indiceDe: (px) => Math.max(0, Math.min(horas, Math.round(((px - IZQ) / w) * 24))) });
  const hm = p.i;
  const fila = (k: DiaSala, texto: string, marca: string, forma: FilaEtiqueta['forma'], tenue: boolean): FilaEtiqueta[] => {
    const v = hm === null ? undefined : acumulado[k][hm];
    if (v === undefined) return [];
    // El último punto de hoy es «ahora», no la hora en punto.
    const ahora = k === 'hoy' && hm === ultimoHoy && horaHoy !== null && horaHoy < hm;
    return [{ etiqueta: ahora ? `${texto} · ${horaUtc(horaHoy!, referencia)}` : texto, valor: formato(v), marca, forma, tenue }];
  };
  const filas =
    hm === null
      ? []
      : [
          ...fila('hoy', t('salaControl.hoy'), 'bg-oficina-te-necesita', 'linea', false),
          ...fila('ayer', t('salaControl.ayerLeyenda'), 'bg-oficina-texto3', 'linea', true),
          ...fila('sem', t('salaControl.semanaLeyenda'), 'border-oficina-esperando', 'discontinua', true),
        ];
  const dh = hm === null ? null : delta(acumulado.hoy[hm] ?? null, acumulado.ayer[hm] ?? null, false);
  return (
    <div ref={ref} className="h-full w-full">
      <div {...p.props} className={cn('relative h-full', FOCO)} aria-label={etiqueta}>
      <svg viewBox={`0 0 ${ancho} ${alto}`} className={tam ? 'block h-full w-full' : 'block h-auto w-full'} role="img" aria-label={etiqueta}>
        {hm !== null ? <line x1={x(hm)} x2={x(hm)} y1={ARRIBA} y2={alto - ABAJO} className="stroke-oficina-texto3" /> : null}
        {marcas.map((v) => (
          <g key={v}>
            <line x1={IZQ} x2={ancho - DER} y1={y(v)} y2={y(v)} className="stroke-oficina-linea" />
            <text x={IZQ - 8} y={y(v) + 4} textAnchor="end" className="fill-oficina-texto3 text-o-nota">
              {formatoEje(v)}
            </text>
          </g>
        ))}
        {/* 10-10 (Pau): en una caja estrecha las horas cada 3 h se pisaban («00:0003:00…»): horasEje pasa a cada 6 h. */}
        {horasEje(w).map((hora) => (
          <text key={hora} x={x(hora)} y={alto - 6} textAnchor="middle" className="fill-oficina-texto3 text-o-nota">
            {horaUtc(hora, referencia)}
          </text>
        ))}
        <polyline points={linea('sem')} fill="none" strokeWidth={2} strokeDasharray="5 5" className="stroke-oficina-esperando" />
        <polyline points={linea('ayer')} fill="none" strokeWidth={2} className="stroke-oficina-texto3" />
        <polyline points={linea('hoy')} fill="none" strokeWidth={3.5} strokeLinejoin="round" strokeLinecap="round" className="stroke-oficina-te-necesita" />
        {prevision && ultimoHoy > 0 && valorHoy !== undefined && horaHoy !== null && horaHoy < 24 ? (
          <g data-testid="prevision">
            <polygon
              points={`${x(horaDe('hoy', ultimoHoy))},${y(valorHoy)} ${x(24)},${y(prevision.alto)} ${x(24)},${y(prevision.bajo)}`}
              className="fill-oficina-te-necesita/10"
            />
            <line
              x1={x(horaDe('hoy', ultimoHoy))}
              y1={y(valorHoy)}
              x2={x(24)}
              y2={y(prevision.total)}
              strokeWidth={2}
              strokeDasharray="2 5"
              strokeLinecap="round"
              className="stroke-oficina-te-necesita"
            />
          </g>
        ) : null}
        {ultimoHoy > 0 && valorHoy !== undefined ? (
          <circle cx={x(horaDe('hoy', ultimoHoy))} cy={y(valorHoy)} r={5} strokeWidth={2} className="fill-oficina-te-necesita stroke-oficina-elevada" />
        ) : null}
        {hm !== null
          ? (['sem', 'ayer', 'hoy'] as const).map((k) =>
              acumulado[k][hm] === undefined ? null : (
                <circle
                  key={k}
                  cx={x(horaDe(k, hm))}
                  cy={y(acumulado[k][hm]!)}
                  r={k === 'hoy' ? 5 : 4}
                  strokeWidth={2}
                  className={cn({ hoy: 'fill-oficina-te-necesita', ayer: 'fill-oficina-texto3', sem: 'fill-oficina-esperando' }[k], 'stroke-oficina-elevada')}
                />
              ),
            )
          : null}
      </svg>
      {hm !== null && p.activa && filas.length ? (
        <Etiqueta
          x={x(hm)}
          ancho={ancho}
          arriba={ARRIBA}
          titulo={hastaHora(hm)}
          filas={filas}
          pie={dh && dh.direccion !== 'igual' ? <span className={dh.tono === 'bueno' ? 'text-oficina-trabajando' : 'text-oficina-error'}>{`${dh.direccion === 'sube' ? '↑' : '↓'} ${dh.texto} ${t('salaControl.grafica.frenteAyer')}`}</span> : undefined}
        />
      ) : null}
      </div>
    </div>
  );
}
